/**
 * Employer-facing Stiff and Sore aggregate (#72).
 *
 * The headline is `earlySignalPercent`: the share of a workforce with at least
 * one region where capacity is still intact but an ache is present. It is the
 * number that goes on the Report of Findings slide, because it is the number
 * that says *here is what you can still prevent*.
 *
 * Every figure here respects the 10-respondent floor, evaluated on whatever set
 * it is handed. Callers must pass the FILTERED set — a fifty-person cooperative
 * with six administrative staff at one site is identifiable, so location plus
 * job category together must be counted on the intersection, never on either
 * filter alone. The floor is a contractual commitment that appears in client
 * agreements, not a preference.
 */

import { LEAD_MAGNET_MIN_RESPONSES } from "@/lib/leadMagnetAggregate";
import {
  isEarlySignal,
  isNeuroFlag,
  isPersistentLowGrade,
  regionStateFor,
  type RegionSignal,
} from "@/lib/stiffSoreScoring";

/** One respondent's six regions. */
export type RespondentSignals = {
  sessionId: string;
  regions: RegionSignal[];
};

export type StiffSoreAggregate = {
  respondentCount: number;
  /** True when the cohort is under the floor; every figure below is then null. */
  suppressed: boolean;
  /** THE headline metric. Percent of respondents with >=1 region in early_signal. */
  earlySignalPercent: number | null;
  /** Percent with any region reported worse than a year ago. */
  trendingWorsePercent: number | null;
  /** Percent with any region noticed 5+ days out of 7. */
  persistentPercent: number | null;
  /** Count of respondents in a masking state on any region. */
  maskingCount: number | null;
  /**
   * Count of respondents reporting numb or tingling on any region.
   * A COUNT ONLY, never a list. There is no drill-through from here to a name.
   */
  neuroReferralCount: number | null;
  /** Mean worst-score per region across the cohort, v2.1 data only. */
  meanWorstByRegion: Record<string, number | null>;
};

const EMPTY: Omit<StiffSoreAggregate, "respondentCount" | "suppressed"> = {
  earlySignalPercent: null,
  trendingWorsePercent: null,
  persistentPercent: null,
  maskingCount: null,
  neuroReferralCount: null,
  meanWorstByRegion: {},
};

function pct(hits: number, total: number): number {
  return Math.round((hits / total) * 1000) / 10;
}

export function buildStiffSoreAggregate(
  respondents: RespondentSignals[],
  minResponses: number = LEAD_MAGNET_MIN_RESPONSES
): StiffSoreAggregate {
  const n = respondents.length;
  if (n < minResponses) {
    // Suppressed, not zero. A caller must be able to tell "too few people to
    // report on" from "nobody has an early signal" — they mean opposite things
    // and only one of them is good news.
    return { respondentCount: n, suppressed: true, ...EMPTY };
  }

  const anyRegion = (r: RespondentSignals, p: (s: RegionSignal) => boolean) =>
    r.regions.some(p);

  const meanWorstByRegion: Record<string, number | null> = {};
  const byRegion = new Map<string, number[]>();
  for (const r of respondents) {
    for (const region of r.regions) {
      const list = byRegion.get(region.regionKey) ?? [];
      list.push(region.stiffSoreWorst);
      byRegion.set(region.regionKey, list);
    }
  }
  for (const [key, vals] of byRegion) {
    meanWorstByRegion[key] = vals.length
      ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
      : null;
  }

  return {
    respondentCount: n,
    suppressed: false,
    earlySignalPercent: pct(
      respondents.filter((r) => anyRegion(r, isEarlySignal)).length,
      n
    ),
    trendingWorsePercent: pct(
      respondents.filter((r) => anyRegion(r, (s) => s.trajectory === "worse")).length,
      n
    ),
    persistentPercent: pct(
      respondents.filter((r) => anyRegion(r, isPersistentLowGrade)).length,
      n
    ),
    maskingCount: respondents.filter((r) =>
      anyRegion(r, (s) => regionStateFor(s) === "masking")
    ).length,
    neuroReferralCount: respondents.filter((r) => anyRegion(r, isNeuroFlag)).length,
    meanWorstByRegion,
  };
}

/**
 * Anonymised quote bank from `commitment_cost`.
 *
 * Returns text only. Never attach a name, a location, or a job category to a
 * quote: any one of those identifies a person in a small crew, which is the
 * whole reason the cohort floor exists. Suppressed entirely below the floor.
 */
export function buildQuoteBank(
  quotes: Array<string | null | undefined>,
  minResponses: number = LEAD_MAGNET_MIN_RESPONSES
): { suppressed: boolean; quotes: string[] } {
  const cleaned = quotes
    .map((q) => (typeof q === "string" ? q.trim() : ""))
    .filter((q) => q.length > 0);
  // The floor applies to the COHORT, not to how many people happened to write
  // something. Nine respondents of whom all nine wrote a quote is still nine
  // people, and still identifiable.
  if (quotes.length < minResponses) return { suppressed: true, quotes: [] };
  return { suppressed: false, quotes: cleaned };
}

/* -------------------------------------------------------------------------
 * Sleep Disturbance, employer side (v2.2)
 *
 * PROVISIONAL PRESENTATION. The numbers below are correct and safe to rely on;
 * how they are shown to an employer is not designed yet. See the tracking issue
 * referenced from SleepAggregatePanel. Do not build further reporting on the
 * assumption that the current layout is final.
 * ---------------------------------------------------------------------- */

export type SleepBandKey =
  | "sleeping_well"
  | "minor"
  | "disrupted"
  | "badly"
  | "severe";

export type SleepRespondent = {
  sleepRawScore: number | null | undefined;
  sleepBand: SleepBandKey | null | undefined;
  sleepSafetyFlag: boolean | null | undefined;
};

export type SleepAggregate = {
  /** Respondents who actually answered the module. Pre-v2.2 sessions are excluded. */
  answeredCount: number;
  /** True when too few answered to report. Every figure below is then null. */
  suppressed: boolean;
  meanScore: number | null;
  /** Percent scoring 9 or more — "Disrupted" or worse. The headline. */
  disruptedPercent: number | null;
  bandCounts: Record<SleepBandKey, number> | null;
  /**
   * Respondents who reported nodding off, or nearly, during the working day.
   * A COUNT ONLY. There is no path from here to a name, and there must not be.
   */
  safetyFlagCount: number | null;
};

const EMPTY_SLEEP: Omit<SleepAggregate, "answeredCount" | "suppressed"> = {
  meanScore: null,
  disruptedPercent: null,
  bandCounts: null,
  safetyFlagCount: null,
};

/** Score at or above which sleep is "Disrupted" or worse. Matches the bands. */
const DISRUPTED_FROM = 9;

export function buildSleepAggregate(
  respondents: SleepRespondent[],
  minResponses: number = LEAD_MAGNET_MIN_RESPONSES
): SleepAggregate {
  // Only sessions that answered the module count toward the denominator.
  // Including pre-v2.2 sessions would silently dilute every percentage with
  // people who were never asked.
  const answered = respondents.filter(
    (r) => typeof r.sleepRawScore === "number" && Number.isFinite(r.sleepRawScore)
  );
  const n = answered.length;
  if (n < minResponses) {
    return { answeredCount: n, suppressed: true, ...EMPTY_SLEEP };
  }

  const bandCounts: Record<SleepBandKey, number> = {
    sleeping_well: 0,
    minor: 0,
    disrupted: 0,
    badly: 0,
    severe: 0,
  };
  let total = 0;
  let disrupted = 0;
  let flagged = 0;
  for (const r of answered) {
    const score = r.sleepRawScore as number;
    total += score;
    if (score >= DISRUPTED_FROM) disrupted += 1;
    if (r.sleepBand && r.sleepBand in bandCounts) bandCounts[r.sleepBand] += 1;
    if (r.sleepSafetyFlag === true) flagged += 1;
  }

  return {
    answeredCount: n,
    suppressed: false,
    meanScore: Math.round((total / n) * 10) / 10,
    disruptedPercent: Math.round((disrupted / n) * 1000) / 10,
    bandCounts,
    safetyFlagCount: flagged,
  };
}
